import { post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { unwrapRealtimePayload } from "../../constants/messagesRealtime"
import { getUserAvatarUrl } from "../../helpers/userAvatar"

export const QUICK_REACTIONS = ["🎬", "🎵", "🔥", "❤️", "👏"]

export const ALLOWED_MESSAGE_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
  "text/csv",
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
]

export const MESSAGE_MEDIA_ACCEPT = ALLOWED_MESSAGE_MEDIA_TYPES.join(",")

export const MESSAGE_MEDIA_TYPES_HINT =
  "JPEG, PNG, WebP, GIF, PDF, CSV, MP4, MOV, AVI, or WMV"

export const MESSAGE_MEDIA_UNSUPPORTED_MSG = `Unsupported file type. Please use ${MESSAGE_MEDIA_TYPES_HINT}.`

export const MESSAGE_MEDIA_ATTACH_TOOLTIP = `Attach a file (${MESSAGE_MEDIA_TYPES_HINT})`

const stripHtml = value =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()

const personName = person =>
  [person?.first_name, person?.last_name].filter(Boolean).join(" ").trim() ||
  person?.name ||
  person?.full_name ||
  person?.email ||
  ""

const mediaTypeFromMime = mimeType => {
  const mime = String(mimeType || "").toLowerCase()
  if (mime.startsWith("image/")) return "image"
  if (mime.startsWith("video/")) return "video"
  if (mime === "application/pdf") return "document"
  if (mime === "text/csv") return "document"
  return "file"
}

const mediaUrl = media =>
  media?.url ||
  media?.full_url ||
  media?.temporary_url ||
  media?.original_url ||
  media?.path ||
  ""

const mapMedia = media => {
  const url = mediaUrl(media)
  const mime = String(media?.mime_type || media?.mime || "").toLowerCase()
  const name = media?.name || media?.file_name || url.split("/").pop() || "File"
  return {
    id: media?.id || media?.temp_media_id || url,
    name,
    url,
    mime,
    isImage: mime.startsWith("image/") || /\.(gif|jpe?g|png|webp)$/i.test(url),
  }
}

const messageMedia = raw => {
  const rows = raw?.medias || raw?.media || raw?.attachments || []
  return (Array.isArray(rows) ? rows : [rows]).filter(Boolean).map(mapMedia)
}

export const normalizeEmoji = value => String(value || "").replace(/\uFE0F/g, "")

const normalizeReactionItem = item => {
  if (!item) return null
  if (typeof item === "string") {
    return item ? { emoji: item, count: 1 } : null
  }
  const emoji = item.emoji || item.reaction || item.type || ""
  if (!emoji) return null
  const rawCount = item.count ?? item.total
  const count =
    rawCount == null || rawCount === ""
      ? 1
      : Number(rawCount)
  if (!Number.isFinite(count) || count <= 0) return null
  return { emoji, count }
}

export const mapReactionSummary = summary => {
  if (!Array.isArray(summary)) return null
  const merged = new Map()
  summary
    .map(normalizeReactionItem)
    .filter(Boolean)
    .forEach(item => {
      const key = normalizeEmoji(item.emoji)
      const previous = merged.get(key)
      if (previous) {
        merged.set(key, {
          emoji: previous.emoji,
          count: previous.count + item.count,
        })
      } else {
        merged.set(key, item)
      }
    })
  return Array.from(merged.values())
}

const mapReactions = raw => {
  const source =
    (Array.isArray(raw?.reactions) && raw.reactions.length > 0
      ? raw.reactions
      : null) ||
    (Array.isArray(raw?.reaction_summary) && raw.reaction_summary.length > 0
      ? raw.reaction_summary
      : null) ||
    []
  return mapReactionSummary(source) || []
}

const getMyReaction = message => {
  const value =
    message?.my_reaction ||
    message?.myReaction ||
    message?.user_reaction ||
    message?.current_user_reaction ||
    message?.reacted ||
    ""
  if (typeof value === "string") return value
  return value?.emoji || value?.reaction || ""
}

/** Same count math as project messages — swap/remove without double-counting. */
export const replaceReactionCounts = (
  reactions,
  previousReaction,
  nextReaction
) => {
  const next = (Array.isArray(reactions) ? reactions : []).map(item => ({
    ...item,
  }))

  if (previousReaction && previousReaction !== nextReaction) {
    const previousIndex = next.findIndex(
      item => normalizeEmoji(item.emoji) === normalizeEmoji(previousReaction)
    )
    if (previousIndex >= 0) {
      const remaining = Number(next[previousIndex].count || 0) - 1
      if (remaining > 0) {
        next[previousIndex] = { ...next[previousIndex], count: remaining }
      } else {
        next.splice(previousIndex, 1)
      }
    }
  }

  if (nextReaction && previousReaction !== nextReaction) {
    const nextIndex = next.findIndex(
      item => normalizeEmoji(item.emoji) === normalizeEmoji(nextReaction)
    )
    if (nextIndex >= 0) {
      next[nextIndex] = {
        ...next[nextIndex],
        count: Number(next[nextIndex].count || 0) + 1,
      }
    } else {
      next.push({ emoji: nextReaction, count: 1 })
    }
  }

  return next
}

export const mapDirectChatMessage = (message, currentUser) => {
  const raw = unwrapRealtimePayload(message) || message || {}
  const sender = raw.sender || raw.user || {}
  const senderId = String(sender.id || raw.sender_id || raw.user_id || "")
  const currentUserId = String(currentUser?.id || "")
  const body = raw.message || raw.body || raw.content || ""
  const sentAt = raw.sent_at || raw.created_at || new Date().toISOString()
  const sentDate = new Date(sentAt)
  const time = Number.isNaN(sentDate.getTime())
    ? ""
    : sentDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })

  const isMe = Boolean(currentUserId && senderId === currentUserId)

  return {
    id: raw.id || raw.uuid,
    senderId,
    sender: isMe
      ? "You"
      : personName(sender) || personName(currentUser) || "",
    avatar: isMe
      ? getUserAvatarUrl(currentUser) || getUserAvatarUrl(sender)
      : getUserAvatarUrl(sender),
    text: stripHtml(body),
    medias: messageMedia(raw),
    reactions: mapReactions(raw),
    myReaction: getMyReaction(raw),
    sentAt,
    time,
    isMe,
  }
}

export const mergeDirectChatMessages = (current, incoming) => {
  const merged = new Map()
  ;[...(Array.isArray(current) ? current : [])]
    .filter(item => item?.id)
    .forEach(item => {
      merged.set(String(item.id), item)
    })
  ;[...(Array.isArray(incoming) ? incoming : [])]
    .filter(item => item?.id)
    .forEach(item => {
      const prev = merged.get(String(item.id))
      if (!prev) {
        merged.set(String(item.id), item)
        return
      }
      const incomingHasReactions =
        Array.isArray(item.reactions) && item.reactions.length > 0
      const keepReactions =
        !incomingHasReactions &&
        Array.isArray(prev.reactions) &&
        prev.reactions.length > 0

      merged.set(String(item.id), {
        ...prev,
        ...item,
        medias:
          Array.isArray(item.medias) && item.medias.length > 0
            ? item.medias
            : prev.medias,
        // Thin realtime/message updates often ship empty reaction fields —
        // keep existing chips unless the incoming payload has reactions.
        reactions: keepReactions ? prev.reactions : item.reactions || [],
        myReaction: keepReactions
          ? item.myReaction || prev.myReaction || ""
          : item.myReaction || "",
      })
    })
  return Array.from(merged.values()).sort(
    (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
  )
}

const normalizeUploadedMedias = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  if (Array.isArray(result?.medias)) return result.medias
  if (result && typeof result === "object" && result.url) return [result]
  return []
}

const mapUploadedMedia = (item, fallbackFile) => {
  const mimeType = item?.mime_type || item?.mimeType || fallbackFile?.type || ""
  return {
    temp_media_id: item?.temp_media_id || item?.tempMediaId || item?.id || "",
    url: item?.url || "",
    path: item?.path || item?.original_path || "",
    disk: item?.disk || "s3",
    mime_type: mimeType,
    type: item?.type || mediaTypeFromMime(mimeType),
    size: Number(item?.size ?? fallbackFile?.size ?? 0) || 0,
    name: item?.name || fallbackFile?.name || "Attachment",
  }
}

/** Same upload contract as project messages. */
export const uploadDirectChatFiles = async files => {
  const formData = new FormData()
  files.forEach(file => {
    formData.append("files[]", file)
  })

  const response = await post(endpoints.uploadMessageMedias, formData, {
    skipErrorToast: true,
  })

  if (!response?.data?.success) {
    const error = new Error("Failed to upload media")
    error.response = { data: response?.data }
    error.payload = response?.data || response
    throw error
  }

  const uploaded = normalizeUploadedMedias(response.data.result).map(
    (item, index) => mapUploadedMedia(item, files[index])
  )

  if (uploaded.length === 0) {
    throw new Error("No media returned from upload")
  }

  return {
    uploaded,
    message: response?.data?.message || "Media uploaded successfully.",
  }
}

/** Same send payload shape as project messages. */
export const toDirectChatMediaPayload = medias =>
  (Array.isArray(medias) ? medias : [])
    .filter(item => item && !item.isUploading && item.url && item.path)
    .map(({ temp_media_id, url, path, disk, mime_type, type, size }) => ({
      ...(temp_media_id ? { temp_media_id } : {}),
      url,
      path,
      disk,
      mime_type,
      type,
      size,
    }))
